import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";

// Import your components
import AdminSidebar from "./AdminSidebar";
import AdminHeader from "./AdminHeader";
import { base_url } from "./AdminUsers";

const EditSlider = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(!(window.innerWidth < 768));
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [formData, setFormData] = useState({
        title: '',
        status: 'Active',
        type: 'Web',
        attachment: null,
        previewUrl: ''
    });
    const [error, setError] = useState(null);

    // Toggle sidebar
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    useEffect(() => {
        const fetchSliderData = async () => {
            const auth = localStorage.getItem("admin");
            try {
                setIsLoading(true);
                const response = await axios.post(
                    `${base_url}/api/webapi/getBannerById`,
                    { id },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `${auth}`,
                        },
                    }
                );

                if (response.data.success) {
                    const sliderData = response.data.data;
                    setFormData({
                        title: sliderData.title,
                        status: sliderData.status,
                        type: sliderData.type,
                        previewUrl: base_url + sliderData.attachment
                    });
                } else {
                    setError(response.data.message || "Failed to fetch slider data");
                    toast.error(response.data.message || "Failed to fetch slider data");
                    navigate("/admin/slider");
                }
            } catch (error) {
                console.error("Error fetching slider data:", error);
                setError("An error occurred. Please try again.");
                toast.error("An error occurred while fetching data. Please try again.");
                navigate("/admin/slider");
            } finally {
                setIsLoading(false);
            }
        };

        fetchSliderData();
    }, [id, navigate]);

    useEffect(() => {
        // Warn about unsaved changes
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        setHasUnsavedChanges(true);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            const reader = new FileReader();
            
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    attachment: selectedFile,
                    previewUrl: reader.result
                }));
            };
            
            reader.readAsDataURL(selectedFile);
            setHasUnsavedChanges(true);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Basic validation
        if (!formData.title.trim()) {
            toast.error("Title is required");
            return;
        }

        setIsSubmitting(true);
        const formDataToSend = new FormData();
        formDataToSend.append('id', id);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('status', formData.status);
        formDataToSend.append('type', formData.type);

        if (formData.attachment) {
            formDataToSend.append('attachment', formData.attachment);
        }

        try {
            const auth = localStorage.getItem('admin');
            const response = await axios.post(
                `${base_url}/api/webapi/updateBanner`,
                formDataToSend,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `${auth}`,
                    },
                }
            );

            if (response.data.success) {
                toast.success("Slider updated successfully");
                setHasUnsavedChanges(false);
                navigate("/admin/slider");
            } else {
                toast.error(response.data.message || "Failed to update slider");
            }
        } catch (error) {
            console.error('Error updating slider:', error);
            toast.error('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const handleResize = () => {
          const mobile = window.innerWidth < 768;
          setIsMobile(mobile);
          
          // On desktop, sidebar should always be "open"
          if (!mobile) {
            setIsSidebarOpen(true);
          }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      }, []);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger m-5">
                {error}
            </div>
        );
    }


    
    return (
        <div className="wrapper">
            <AdminSidebar 
          isSidebarOpen={isSidebarOpen} 
          toggleSidebar={toggleSidebar} 
          isMobile={isMobile}
        />
        
        {/* Header - Pass mobile info */}
        <AdminHeader 
          toggleSidebar={toggleSidebar} 
          isSidebarOpen={isSidebarOpen}
          isMobile={isMobile}
        />
        
        {/* Content Wrapper - Use conditional class and margin */}
        <div
          className={`content-wrapper ${
            !isMobile && isSidebarOpen ? "" : "open-content-wrapper"
          }`}
          style={{
            marginLeft: (!isMobile && isSidebarOpen) ? "" : "0",
            transition: "margin-left 0.3s ease"
          }}
        >
                <div className="container-fluid">
                    <h1 className="page-title">
                        <i className="fa fa-edit"></i> Edit Slider
                    </h1>
                    
                    <div className="page-content mt-4 browse">
                        <div className="row">
                            <div className="col-md-12 oneonofftab">
                                <div className="panel panel-bordered p-3">
                                    <div className="panel-heading">
                                        <h3 className="panel-title">Update Slider Information</h3>
                                    </div>
                                    <div className="panel-body">
                                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                                            <div className="form-group mb-3">
                                                <label htmlFor="title">Title <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="title"
                                                    id="title"
                                                    value={formData.title}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>

                                            <div className="form-group mb-3">
                                                <label htmlFor="status">Status</label>
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    id="status"
                                                    value={formData.status}
                                                    onChange={handleChange}
                                                >
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                </select>
                                            </div>

                                            <div className="form-group mb-3">
                                                <label htmlFor="type">Type</label>
                                                <select
                                                    className="form-control"
                                                    name="type"
                                                    id="type"
                                                    value={formData.type}
                                                    onChange={handleChange}
                                                >
                                                    <option value="Web">Web</option>
                                                    <option value="Mobile">Mobile</option>
                                                </select>
                                            </div>

                                            <div className="form-group mb-3">
                                                <label htmlFor="attachment">Attachment</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/*"
                                                    name="attachment"
                                                    id="attachment"
                                                    onChange={handleFileChange}
                                                />
                                                {formData.previewUrl && (
                                                    <div className="mt-2">
                                                        <p>Preview:</p>
                                                        <img
                                                            src={formData.previewUrl}
                                                            alt="Preview"
                                                            style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                            onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-image.jpg' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group mt-4">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                            />
                                                            {' '}Updating...
                                                        </>
                                                    ) : (
                                                        'Update Slider'
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary ms-2"
                                                    onClick={() => navigate('/admin/slider')}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditSlider;